import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { track, whatsappLink, quoteLink } from "@/lib/site";
import { JsonLd, breadcrumbLd } from "./Seo";

export const Container = ({ className, children, ...rest }) => (
  <div className={cn("mx-auto w-full max-w-[76rem] px-5 sm:px-8", className)} {...rest}>
    {children}
  </div>
);

export const Eyebrow = ({ children, className, light }) => (
  <p className={cn("kl-eyebrow", light ? "text-white/65" : "text-black/55", className)}>{children}</p>
);

export const SectionHeading = ({ eyebrow, title, lead, as: H = "h2", className, light, align = "left", id }) => (
  <div className={cn("max-w-2xl", align === "center" && "mx-auto text-center", className)}>
    {eyebrow ? <Eyebrow light={light}>{eyebrow}</Eyebrow> : null}
    <H id={id} className={cn("kl-h2 mt-3", light ? "text-white" : "text-[#161616]")}>{title}</H>
    {lead ? <p className={cn("mt-4 text-base leading-7 sm:text-lg", light ? "text-white/70" : "text-black/65")}>{lead}</p> : null}
  </div>
);

// Subtle reveal on scroll; disabled automatically for prefers-reduced-motion via CSS.
export const Reveal = ({ children, className, delay = 0, as: Tag = "div" }) => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setShown(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={cn("kl-reveal", shown && "is-in", className)} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </Tag>
  );
};

export const Img = ({ media, sizes = "(min-width: 1024px) 40vw, 90vw", className, eager = false, ...rest }) => (
  <img
    src={media.src}
    srcSet={media.srcSet}
    sizes={sizes}
    alt={media.alt}
    loading={eager ? "eager" : "lazy"}
    fetchPriority={eager ? "high" : undefined}
    decoding="async"
    className={cn("h-full w-full object-cover", className)}
    {...rest}
  />
);

export const PhotoFrame = ({ media, ratio = "4/3", className, sizes, eager, imgClassName, testId }) => (
  <figure className={cn("kl-mat", className)} data-testid={testId}>
    <div className="kl-mat-inner" style={{ aspectRatio: ratio }}>
      <Img media={media} sizes={sizes} eager={eager} className={cn("kl-photo", imgClassName)} />
    </div>
  </figure>
);

export const MonogramPlaceholder = ({ ratio = "4/3", label = "Photos coming soon", className }) => (
  <div className={cn("kl-mat", className)} data-testid="photos-coming-soon-placeholder">
    <div className="kl-mat-inner kl-placeholder grid place-items-center" style={{ aspectRatio: ratio }} role="img" aria-label={`KhozaLand monogram — ${label}`}>
      <div className="text-center">
        <img src="/images/khozaland-logo-sm.png" alt="" aria-hidden="true" className="mx-auto w-24 opacity-30" loading="lazy" width="96" height="70" />
        <p className="kl-eyebrow mt-3 text-black/45">{label}</p>
      </div>
    </div>
  </div>
);

export const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={cn("h-4 w-4", className)} fill="currentColor">
    <path d="M19.05 4.91A9.82 9.82 0 0 0 12.04 2C6.6 2 2.17 6.43 2.17 11.87c0 1.74.46 3.44 1.32 4.94L2.09 22l5.33-1.4a9.86 9.86 0 0 0 4.62 1.18h.01c5.44 0 9.87-4.43 9.87-9.87a9.8 9.8 0 0 0-2.87-7Zm-7.01 15.2h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.16.83.84-3.08-.2-.32a8.17 8.17 0 0 1-1.25-4.34c0-4.52 3.68-8.2 8.21-8.2a8.2 8.2 0 0 1 8.2 8.21c0 4.52-3.68 8.2-8.15 8.23Zm4.5-6.14c-.25-.12-1.46-.72-1.69-.8-.23-.08-.39-.12-.56.12-.16.25-.64.8-.78.97-.14.16-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.7-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.42h-.48a.92.92 0 0 0-.66.31c-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.16 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.29Z" />
  </svg>
);

export const QuoteButton = ({ label = "Request a Quote", category, product, type, className, testId = "primary-quote-button", variant = "dark", to }) => (
  <Link
    to={to || quoteLink({ category, product, type })}
    onClick={() => track("quote_cta_click", { label, category })}
    className={cn(variant === "dark" ? "kl-btn kl-btn-dark" : variant === "light" ? "kl-btn kl-btn-light" : "kl-btn kl-btn-outline", className)}
    data-testid={testId}
  >
    {label}
    <ArrowRight className="h-4 w-4" aria-hidden="true" />
  </Link>
);

export const WhatsAppButton = ({ label = "WhatsApp KhozaLand", messageKey = "general", message, className, testId = "whatsapp-cta-button", variant = "outline" }) => (
  <a
    href={whatsappLink(messageKey, message)}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => track("whatsapp_click", { label: messageKey })}
    className={cn(variant === "outline" ? "kl-btn kl-btn-outline" : variant === "ghost-light" ? "kl-btn kl-btn-ghost-light" : "kl-btn kl-btn-dark", className)}
    data-testid={testId}
    aria-label={`${label} (opens WhatsApp in a new tab)`}
  >
    <WhatsAppIcon />
    {label}
  </a>
);

export const TextLink = ({ to, children, className, testId, light }) => (
  <Link to={to} className={cn("group inline-flex items-center gap-1.5 text-sm font-semibold", light ? "text-white" : "text-[#161616]", className)} data-testid={testId}>
    <span className="kl-underline">{children}</span>
    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
  </Link>
);

export const Breadcrumbs = ({ items }) => (
  <>
    <nav aria-label="Breadcrumb" className="text-[13px] text-black/55" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1">
        <li><Link to="/" className="hover:text-black">Home</Link></li>
        {items.map((it, i) => (
          <li key={it.path} className="flex items-center gap-1">
            <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            {i === items.length - 1 ? <span aria-current="page" className="text-black/80">{it.name}</span> : <Link to={it.path} className="hover:text-black">{it.name}</Link>}
          </li>
        ))}
      </ol>
    </nav>
    <JsonLd data={breadcrumbLd(items)} />
  </>
);
